import { Controller, useFormContext } from 'react-hook-form';
import { TextInputBase } from '../components/text-input-base';
import { EmailInputBase } from '../components/email-input-base';
import { NameEmailQuestionProps } from '../../types';
import useVisitorSession from '@/hooks/useVisitorSession';
import { useCallback, useRef } from 'react';
import authService from '@/services/auth.service';
import { isValidEmail } from '@/utils/common';

export const NameEmailQuestion = ({ question }: NameEmailQuestionProps) => {
  const { control } = useFormContext();
  const { captureLeadAndName } = useVisitorSession();
  const lastCapturedRef = useRef<{ email: string; name: string } | null>(null);

  const handleEmailCapture = useCallback(
    async (email: string, name: string) => {
      // Only skip if both email AND name are exactly the same as last capture
      if (
        lastCapturedRef.current?.email === email &&
        lastCapturedRef.current?.name === name
      ) {
        return;
      }

      try {
        // Get invite code from localStorage if present
        const inviteCode = authService.getInviteCode();

        // Capture Lead and Name
        await captureLeadAndName({
          email,
          inviteCode: inviteCode || undefined,
          firstName: name || undefined,
        });

        // Update last captured values
        lastCapturedRef.current = { email, name };
      } catch (err: any) {
        const status = err?.response?.status || err?.status;

        // Handle different error types appropriately
        if (status === 400 || status === 401) {
          // 400/401: Expected errors - silently ignore
          return;
        } else if (status >= 500) {
          // 5xx: Server errors - log as errors
          console.error('Lead capture server error:', err);
        } else {
          // Other errors (network issues, etc.)
          console.error('Failed to capture lead and name:', err);
        }
      }
    },
    [captureLeadAndName]
  );

  const attemptLeadCapture = useCallback(
    (email: string, name: string) => {
      // Only attempt capture if both fields are filled AND email is valid
      if (name && email) {
        if (isValidEmail(email)) {
          handleEmailCapture(email, name);
        }
      }
    },
    [handleEmailCapture]
  );

  return (
    <Controller
      control={control}
      name={question.slug}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const currentValue = value || { name: '', email: '' };

        // Extract specific field errors - error can be either a simple FieldError or nested errors
        const nameError = (error as any)?.name?.message;
        const emailError = (error as any)?.email?.message;
        const generalError = error?.message;

        const handleNameChange = (newName: string) => {
          const newValue = {
            ...currentValue,
            name: newName
          };
          onChange(newValue);
          attemptLeadCapture(newValue.email, newValue.name);
        };

        const handleEmailChange = (newEmail: string) => {
          const newValue = {
            ...currentValue,
            email: newEmail
          };
          onChange(newValue);
          attemptLeadCapture(newValue.email, newValue.name);
        };

        return (
          <div className="questionnaire-question-content">
            <div className="flex flex-col gap-4">
              <TextInputBase
                value={currentValue.name}
                onChange={handleNameChange}
                placeholder="Enter your name"
                error={nameError || generalError}
              />
              <EmailInputBase
                value={currentValue.email}
                onChange={handleEmailChange}
                placeholder="Enter your email"
                error={emailError || generalError}
              />
              {(generalError || nameError || emailError) && (
                <div className="text-red-500 text-sm mt-1">
                  {generalError || emailError || nameError}
                </div>
              )}
            </div>
          </div>
        );
      }}
      rules={{
        required: question.required ? 'Both name and email are required' : false,
        validate: (value) => {
          // Always validate if required OR if value has been provided
          const val = value || { name: '', email: '' };

          // Check if either field has content - if so, validate both
          const hasContent = val.name?.trim() || val.email?.trim();

          if (question.required || hasContent) {
            // Both fields must be filled
            if (!val.name?.trim() || !val.email?.trim()) {
              return 'Both name and email are required';
            }

            // Email format validation
            if (!isValidEmail(val.email)) {
              return 'Please enter a valid email address';
            }
          }

          return true;
        }
      }}
      defaultValue={{ name: '', email: '' }}
    />
  );
};
